import { makeAutoObservable } from 'mobx';
import { DO_CHARGE_MESSAGE, READ_MESSAGE_STATS } from 'network/shared/message';
import {
  READ_CONTENT_POINT_SETTING,
  UPDATE_CONTENT_POINT_SETTING,
  DO_CHARGE_CONTENT_POINTS,
  READ_ATT_SETTING,
  READ_BILL_SETTING,
  READ_CHAT_SETTING,
  READ_HOMEWORK_SETTING,
  READ_MESSAGE_SETTING,
  READ_TEXTBOOK_SETTING,
  UPDATE_ATT_SETTING,
  UPDATE_BILL_SETTING,
  UPDATE_CHAT_SETTING,
  UPDATE_ACADEMY_EDITABLE_SETTING,
  UPDATE_HOMEWORK_SETTING,
  UPDATE_MESSAGE_SETTING,
  UPDATE_TEXTBOOK_SETTING,
} from 'network/shared/setting';
import BaseStore from 'stores/BaseStore';
import SC from 'constants/shared';
import { AcademyEditableSettingT } from 'types/shared/core/Academy';
import { AttSettingT } from 'types/shared/core/Attendance';
import { BillSettingT } from 'types/shared/core/Bill';
import { ChatSettingT } from 'types/shared/core/Chat';
import { HomeworkSettingT } from 'types/shared/core/Homework';
import { TextbookSettingT } from 'types/shared/core/Textbook';
import {
  EditableMessageSettingT,
  MessageSettingT,
  MessageStatsT,
} from 'types/shared/core/Message';
import {
  EmptyRequestT,
  SettingSingleFieldUpdateRequestT,
  SettingUpdateRequestT,
} from 'types/shared/reqAndRes/CommonRequest';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import { TextbookSettingUpdateRequestT } from 'types/shared/reqAndRes/Textbook';
import { MyError } from 'utils/MyError';
import { api } from 'utils/api/api';
import { getActiveAcademyId } from 'stores/root/RootStore';
import {
  ContentPointSettingDetailT,
  ContentPointChargePackT,
  ContentPointSettingT,
} from 'types/shared/core/Content';

const messageSettingCache = new Map<number, MessageSettingT>();

export default class SettingDataStore implements BaseStore {
  readContentPointSetting = async (): Promise<MyResponse<ContentPointSettingDetailT> | null> => {
    try {
      return await api<EmptyRequestT, MyResponse<ContentPointSettingDetailT>>(
        READ_CONTENT_POINT_SETTING,
        {},
      );
    } catch (error) {
      await MyError.handleCatchedError(error);
      return null;
    }
  };

  updateContentPointSetting = async (setting: ContentPointSettingT): Promise<MyResponse<void> | null> => {
    try {
      return await api<ContentPointSettingT, MyResponse<void>>(UPDATE_CONTENT_POINT_SETTING, setting);
    } catch (error) {
      await MyError.handleCatchedError(error);
      return null;
    }
  };

  doChargeContentPoints = async (
    pack: ContentPointChargePackT,
  ): Promise<MyResponse<void> | null> => {
    try {
      return await api<{ pack: ContentPointChargePackT }, MyResponse<void>>(
        DO_CHARGE_CONTENT_POINTS,
        { pack },
      );
    } catch (error) {
      await MyError.handleCatchedError(error);
      return null;
    }
  };

  constructor() {
    makeAutoObservable(this);
  }

  init = () => {
    // no-op
  };

  private assertSingleServiceSettingKey = (
    payload: Record<string, unknown>,
    allowedKeys: readonly string[],
  ) => {
    const matchedKeys = allowedKeys.filter((key) => payload?.[key] !== undefined);
    if (matchedKeys.length !== 1) {
      throw new Error('invalid service setting payload');
    }
  };

  //
  // 출결설정
  //
  readAttSetting = async (): Promise<MyResponse<AttSettingT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<AttSettingT>>(READ_ATT_SETTING, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateAttSetting = async <P extends keyof AttSettingT>(
    attSetting: SettingSingleFieldUpdateRequestT<AttSettingT, P>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        attSetting as Record<string, unknown>,
        SC.ServiceSetting.Attendance.updatableKeys,
      );

      const result = await api<SettingSingleFieldUpdateRequestT<AttSettingT, P>, MyResponse<void>>(
        UPDATE_ATT_SETTING,
        attSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 학원비설정
  //
  readBillSetting = async (): Promise<MyResponse<BillSettingT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<BillSettingT>>(READ_BILL_SETTING, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateBillSetting = async <P extends keyof BillSettingT>(
    billSetting: SettingSingleFieldUpdateRequestT<BillSettingT, P>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        billSetting as Record<string, unknown>,
        SC.ServiceSetting.Bill.updatableKeys,
      );

      const result = await api<
        SettingSingleFieldUpdateRequestT<BillSettingT, P>,
        MyResponse<void>
      >(
        UPDATE_BILL_SETTING,
        billSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 메시지설정
  //
  getCachedMessageSetting = (academyId: number): MessageSettingT | null => {
    return messageSettingCache.get(academyId) || null;
  };

  readMessageSetting = async (): Promise<MyResponse<MessageSettingT> | null> => {
    const academyId = getActiveAcademyId();

    try {
      const result = await api<EmptyRequestT, MyResponse<MessageSettingT>>(READ_MESSAGE_SETTING, {});
      if (result.isSuccess && result.object) messageSettingCache.set(academyId, result.object);
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateMessageSetting = async (
    messageSetting: SettingUpdateRequestT<EditableMessageSettingT>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        messageSetting as Record<string, unknown>,
        SC.ServiceSetting.Message.updatableKeys,
      );

      const result = await api<SettingUpdateRequestT<EditableMessageSettingT>, MyResponse<void>>(
        UPDATE_MESSAGE_SETTING,
        messageSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  readMessageStats = async (): Promise<MyResponse<MessageStatsT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<MessageStatsT>>(READ_MESSAGE_STATS, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  doChargeMessage = async (): Promise<MyResponse<void> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<void>>(DO_CHARGE_MESSAGE, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 상담톡설정
  //
  readChatSetting = async (): Promise<MyResponse<ChatSettingT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<ChatSettingT>>(READ_CHAT_SETTING, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateChatSetting = async (
    chatSetting: SettingUpdateRequestT<ChatSettingT>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        chatSetting as Record<string, unknown>,
        SC.ServiceSetting.Chat.updatableKeys,
      );

      const result = await api<SettingUpdateRequestT<ChatSettingT>, MyResponse<void>>(
        UPDATE_CHAT_SETTING,
        chatSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 숙제설정
  //
  readHomeworkSetting = async (): Promise<MyResponse<HomeworkSettingT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<HomeworkSettingT>>(READ_HOMEWORK_SETTING, {});
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateHomeworkSetting = async (
    homeworkSetting: SettingUpdateRequestT<HomeworkSettingT>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        homeworkSetting as Record<string, unknown>,
        SC.ServiceSetting.Homework.updatableKeys,
      );

      const result = await api<SettingUpdateRequestT<HomeworkSettingT>, MyResponse<void>>(
        UPDATE_HOMEWORK_SETTING,
        homeworkSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 교재설정
  //
  readTextbookSetting = async (): Promise<MyResponse<TextbookSettingT> | null> => {
    try {
      const result = await api<EmptyRequestT, MyResponse<TextbookSettingT>>(
        READ_TEXTBOOK_SETTING,
        {},
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  updateTextbookSetting = async (
    textbookSetting: TextbookSettingUpdateRequestT,
  ): Promise<MyResponse<void> | null> => {
    try {
      const result = await api<TextbookSettingUpdateRequestT, MyResponse<void>>(
        UPDATE_TEXTBOOK_SETTING,
        textbookSetting,
      );
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };

  //
  // 댓글설정
  //
  updateAcademyEditableSetting = async <P extends keyof AcademyEditableSettingT>(
    payload: SettingSingleFieldUpdateRequestT<AcademyEditableSettingT, P>,
  ): Promise<MyResponse<void> | null> => {
    try {
      this.assertSingleServiceSettingKey(
        payload as Record<string, unknown>,
        SC.AcademyEditableSetting.updatableKeys,
      );

      const result = await api<
        SettingSingleFieldUpdateRequestT<AcademyEditableSettingT, P>,
        MyResponse<void>
      >(UPDATE_ACADEMY_EDITABLE_SETTING, payload);
      return result;
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
    return null;
  };
}
